import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Trash2, Receipt, Car, Upload, Sparkles, Loader2, Save, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CV_OPTIONS = [
  { value: "3", label: "≤ 3 CV" },
  { value: "4", label: "4 CV" },
  { value: "5", label: "5 CV" },
  { value: "6", label: "6 CV" },
  { value: "7", label: "≥ 7 CV" },
];

function newItem(kind) {
  return {
    _local_id: crypto.randomUUID(),
    kind,
    description: "",
    expense_date: new Date().toISOString().slice(0, 10),
    amount_ttc: kind === "receipt" ? "" : null,
    vat_amount: null,
    pcg_code: kind === "receipt" ? "625700" : null,
    file_asset_id: null,
    file_name: null,
    km_distance: kind === "mileage" ? "" : null,
    fiscal_horsepower: kind === "mileage" ? 5 : null,
    electric: false,
    computed_amount: null,
    extracting: false,
  };
}


export default function ExpenseReportEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pcg, setPcg] = useState([]);
  const [scaleMeta, setScaleMeta] = useState({ year: 2026 });
  const [period, setPeriod] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, m] = await Promise.all([
          api.get("/purchases/pcg-categories"),
          api.get("/purchases/mileage-scale"),
        ]);
        setPcg(p.data.categories || []);
        setScaleMeta(m.data.scale?.meta || { year: 2026 });
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const addItem = (kind) => setItems((it) => [...it, newItem(kind)]);
  const removeItem = (id) => setItems((it) => it.filter((x) => x._local_id !== id));
  const updateItem = (id, patch) =>
    setItems((it) => it.map((x) => (x._local_id === id ? { ...x, ...patch } : x)));

  const uploadReceipt = async (item, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateItem(item._local_id, {
        file_asset_id: r.data.file_asset_id,
        file_name: r.data.filename,
      });
      toast.success("Justificatif joint.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const extract = async (item) => {
    if (!item.file_asset_id) return;
    updateItem(item._local_id, { extracting: true });
    try {
      const r = await api.post(`/purchases/receipts/${item.file_asset_id}/extract`);
      updateItem(item._local_id, {
        amount_ttc: r.data.amount_ttc ?? item.amount_ttc,
        expense_date: r.data.expense_date || item.expense_date,
        pcg_code: r.data.pcg_code || item.pcg_code,
        description: item.description || (r.data.supplier_name || ""),
        extracting: false,
      });
      const st = r.data.validation?.status;
      if (st === "error") toast.warning("Extraction imprécise — merci de vérifier les champs.");
      else toast.success("Champs pré-remplis. Relisez avant de soumettre.");
    } catch (e) {
      toast.error(apiError(e));
      updateItem(item._local_id, { extracting: false });
    }
  };

  const computeMileagePreview = (item) => {
    // Approximate — same 2026 scale as backend; final value is server-computed.
    const km = Number(item.km_distance) || 0;
    const cv = Number(item.fiscal_horsepower) || 5;
    const table = {
      3:  { a1: 0.529, a2: 0.316, f: 1065, a3: 0.370 },
      4:  { a1: 0.606, a2: 0.340, f: 1330, a3: 0.407 },
      5:  { a1: 0.636, a2: 0.357, f: 1395, a3: 0.427 },
      6:  { a1: 0.665, a2: 0.374, f: 1457, a3: 0.447 },
      7:  { a1: 0.697, a2: 0.394, f: 1515, a3: 0.470 },
    };
    const row = table[cv <= 3 ? 3 : (cv >= 7 ? 7 : cv)];
    let amt = 0;
    if (km <= 5000) amt = km * row.a1;
    else if (km <= 20000) amt = km * row.a2 + row.f;
    else amt = km * row.a3;
    if (item.electric) amt *= 1.2;
    return Math.round(amt * 100) / 100;
  };

  const total = items.reduce((acc, it) => {
    if (it.kind === "mileage") return acc + computeMileagePreview(it);
    return acc + (Number(it.amount_ttc) || 0);
  }, 0);

  const submit = async () => {
    if (!period.trim()) { toast.error("Renseignez la période."); return; }
    if (items.length === 0) { toast.error(t("expenseReports.editor.empty")); return; }
    for (const it of items) {
      if (!it.description?.trim()) { toast.error("Chaque ligne doit avoir une description."); return; }
      if (it.kind === "receipt" && !(Number(it.amount_ttc) > 0)) {
        toast.error("Chaque justificatif doit avoir un montant."); return;
      }
      if (it.kind === "mileage" && !(Number(it.km_distance) > 0)) {
        toast.error("Chaque trajet doit avoir une distance."); return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        period_label: period.trim(),
        notes: notes.trim() || undefined,
        items: items.map(({ _local_id, extracting, file_name, computed_amount, ...rest }) => ({
          ...rest,
          amount_ttc: rest.amount_ttc != null && rest.amount_ttc !== "" ? Number(rest.amount_ttc) : null,
          km_distance: rest.km_distance != null && rest.km_distance !== "" ? Number(rest.km_distance) : null,
          fiscal_horsepower: rest.fiscal_horsepower != null ? Number(rest.fiscal_horsepower) : null,
        })),
      };
      const r = await api.post("/expense-reports", payload);
      toast.success("Note soumise pour validation.");
      navigate(`/app/expenses/${r.data.report.id}`);
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="expense-report-editor">
      <button className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              onClick={() => navigate("/app/expenses")}
              data-testid="editor-back-btn">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        Retour
      </button>

      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          {t("expenseReports.editor.title")}
        </div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("expenseReports.editor.title")}
        </h1>
      </div>

      <Card className="p-6 border-slate-200 rounded-xl bg-white space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>{t("expenseReports.editor.period")}</Label>
            <Input value={period} onChange={(e) => setPeriod(e.target.value)}
                   placeholder={t("expenseReports.editor.periodPlaceholder")}
                   data-testid="editor-period" />
          </div>
        </div>
        <div>
          <Label>{t("expenseReports.editor.notes")}</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2} data-testid="editor-notes" />
        </div>
      </Card>

      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
              {t("expenseReports.editor.items")}
            </div>
            <div className="text-lg font-medium text-slate-800 mt-1">
              {items.length} ligne{items.length > 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => addItem("receipt")}
                    data-testid="add-receipt-btn">
              <Receipt className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
              {t("expenseReports.editor.addReceipt")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => addItem("mileage")}
                    data-testid="add-mileage-btn">
              <Car className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
              {t("expenseReports.editor.addMileage")}
            </Button>
          </div>
        </div>

        {items.length === 0 && (
          <Card className="p-8 text-center border-dashed border-slate-300 bg-white text-sm text-slate-500"
                data-testid="editor-empty">
            {t("expenseReports.editor.empty")}
          </Card>
        )}

        <div className="space-y-3">
          {items.map((item, idx) => (
            <ItemCard
              key={item._local_id}
              item={item} idx={idx}
              pcg={pcg} scaleMeta={scaleMeta}
              onUpdate={(patch) => updateItem(item._local_id, patch)}
              onRemove={() => removeItem(item._local_id)}
              onUpload={(f) => uploadReceipt(item, f)}
              onExtract={() => extract(item)}
              mileagePreview={computeMileagePreview(item)}
              t={t}
            />
          ))}
        </div>
      </div>

      <Card className="p-6 border-slate-200 rounded-xl bg-white flex items-center justify-between sticky bottom-4 shadow-md"
            data-testid="editor-total-bar">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">
            {t("expenseReports.editor.total")}
          </div>
          <div className="text-2xl font-semibold text-slate-900 mt-0.5"
               style={{ fontFamily: "Manrope, sans-serif" }}
               data-testid="editor-total">
            {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(total)}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => navigate("/app/expenses")}
                  data-testid="editor-cancel-btn">
            {t("expenseReports.editor.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving || items.length === 0}
                  data-testid="editor-submit-btn">
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Envoi…</>
            ) : (
              <><Save className="h-4 w-4 mr-1.5" /> {t("expenseReports.editor.submit")}</>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}


function ItemCard({ item, idx, pcg, scaleMeta, onUpdate, onRemove, onUpload, onExtract, mileagePreview, t }) {
  const isReceipt = item.kind === "receipt";
  const fileInputRef = { current: null };

  return (
    <Card className="p-5 border-slate-200 rounded-xl bg-white space-y-4"
          data-testid={`item-card-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center",
                              isReceipt ? "bg-blue-50" : "bg-emerald-50")}>
            {isReceipt ? (
              <Receipt className="h-4 w-4 text-blue-600" strokeWidth={2} />
            ) : (
              <Car className="h-4 w-4 text-emerald-600" strokeWidth={2} />
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-slate-800">
              #{idx + 1} · {t(`expenseReports.item.${item.kind}`)}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove}
                className="text-slate-500 hover:text-red-600"
                data-testid={`remove-item-${idx}`}>
          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label>{t("expenseReports.item.description")}</Label>
          <Input value={item.description}
                 onChange={(e) => onUpdate({ description: e.target.value })}
                 placeholder={isReceipt ? "Ex : Restaurant client" : "Ex : Déplacement Paris → Lyon"}
                 data-testid={`item-description-${idx}`} />
        </div>
        <div>
          <Label>{t("expenseReports.item.date")}</Label>
          <DatePickerFR value={item.expense_date}
                         onChange={(v) => onUpdate({ expense_date: v })}
                         data-testid={`item-date-${idx}`} />
        </div>

        {isReceipt ? (
          <>
            <div>
              <Label>{t("expenseReports.item.amountTTC")}</Label>
              <Input type="number" step="0.01" value={item.amount_ttc ?? ""}
                     onChange={(e) => onUpdate({ amount_ttc: e.target.value })}
                     data-testid={`item-amount-${idx}`} />
            </div>
            <div className="md:col-span-2">
              <Label>{t("expenseReports.item.pcgCode")}</Label>
              <Select value={item.pcg_code || ""}
                      onValueChange={(v) => onUpdate({ pcg_code: v })}>
                <SelectTrigger data-testid={`item-pcg-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pcg.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="font-mono text-xs mr-2 text-slate-500">{c.code}</span>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Paperclip className="h-3.5 w-3.5 text-slate-500" strokeWidth={2} />
                  {item.file_name ? (
                    <span data-testid={`item-file-name-${idx}`}>{item.file_name}</span>
                  ) : (
                    <span className="text-slate-500">Aucun justificatif</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input id={`item-file-input-${idx}`} name={`item-file-input-${idx}`}
                           type="file" className="hidden"
                           accept="application/pdf,image/*"
                           ref={(el) => (fileInputRef.current = el)}
                           onChange={(e) => onUpload(e.target.files?.[0])}
                           data-testid={`item-file-input-${idx}`} />
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
                        {item.file_name ? "Changer" : t("expenseReports.item.upload")}
                      </span>
                    </Button>
                  </label>
                  {item.file_asset_id && (
                    <Button variant="outline" size="sm" onClick={onExtract}
                            disabled={item.extracting}
                            data-testid={`item-extract-btn-${idx}`}>
                      {item.extracting ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> {t("expenseReports.item.extracting")}</>
                      ) : (
                        <><Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} /> {t("expenseReports.item.extract")}</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <Label>{t("expenseReports.item.km")}</Label>
              <Input type="number" step="0.1" value={item.km_distance ?? ""}
                     onChange={(e) => onUpdate({ km_distance: e.target.value })}
                     data-testid={`item-km-${idx}`} />
            </div>
            <div>
              <Label>{t("expenseReports.item.cv")}</Label>
              <Select value={String(item.fiscal_horsepower || 5)}
                      onValueChange={(v) => onUpdate({ fiscal_horsepower: Number(v) })}>
                <SelectTrigger data-testid={`item-cv-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CV_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch checked={!!item.electric}
                      onCheckedChange={(v) => onUpdate({ electric: v })}
                      data-testid={`item-electric-${idx}`} />
              <Label className="cursor-pointer">
                {t("expenseReports.item.electric")}
              </Label>
            </div>
            <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3"
                 data-testid={`item-mileage-computed-${idx}`}>
              <div className="text-[10px] font-medium uppercase tracking-widest text-emerald-800">
                {t("expenseReports.item.computed")}
              </div>
              <div className="text-2xl font-semibold text-emerald-900 mt-0.5"
                   style={{ fontFamily: "Manrope, sans-serif" }}>
                {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(mileagePreview)}
              </div>
              <div className="text-[11px] text-emerald-700 mt-1">
                {t("expenseReports.item.computeHint", { year: scaleMeta.year || 2026 })}
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
